import type { ContentHref } from '@generalTypes/apiTypes';
import { KovResourcePicker, Modal } from '@kathondvla/react-shared-components/src/components';
import { escapeRegExp } from 'lodash';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useEditHref } from '../../hooks/UseEditHref';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import {
  type KrcGoalRelationItem,
  type KrcGoalRelationOption,
  getContentRelationTypeForKrcRelation,
  selectAllKrcGoalRelations,
  selectKrcCurrentGoalDomainHref,
  selectKrcGoalOptionsExcludingCurrent,
} from '@UI/aside/asideKrcGoalRelations/asideKrcGoalRelationsSelectors';
import type { KrcGoalRelationUiType } from '@nodeTypeConfig/configTypes';

export type KrcGoalRelationModalSubmit = {
  targetHref: ContentHref;
};

export interface KrcGoalRelationModalProps {
  onClose?: () => void;
  onSubmit?: (response: KrcGoalRelationModalSubmit | undefined) => void;
  relation?: KrcGoalRelationItem;
  relationType?: KrcGoalRelationUiType;
  title?: string;
}

const isSameRelation = (
  left: KrcGoalRelationItem,
  right: KrcGoalRelationItem | undefined,
): boolean => {
  return !!right && left.key === right.key;
};

const KrcGoalRelationModal: React.FC<KrcGoalRelationModalProps> = ({
  onClose = () => {},
  onSubmit,
  relation,
  relationType,
  title,
}) => {
  const editHref = useEditHref();
  const goalOptions = useTypedSelector((state) =>
    selectKrcGoalOptionsExcludingCurrent(state, editHref),
  );
  const existingRelations = useTypedSelector((state) => selectAllKrcGoalRelations(state, editHref));
  const currentGoalDomainHref = useTypedSelector((state) =>
    selectKrcCurrentGoalDomainHref(state, editHref),
  );

  const filterGoalOptions = (
    { data }: { data: KrcGoalRelationOption },
    searchTerm: string,
  ): boolean => {
    if (!searchTerm || searchTerm.trim().length < 3) {
      return !currentGoalDomainHref || data.domainHref === currentGoalDomainHref;
    }

    const regExp = new RegExp(escapeRegExp(searchTerm), 'gi');
    return regExp.test(data.searchValue);
  };

  const [targetHref, setTargetHref] = useState<ContentHref | null>(relation?.targetHref || null);

  useEffect(() => {
    setTargetHref(relation?.targetHref || null);
  }, [relation]);

  const filteredGoalOptions = goalOptions.filter((option) => {
    if (!relationType) {
      return false;
    }

    const selectedRelationType = getContentRelationTypeForKrcRelation(relationType);

    const duplicateRelation = existingRelations.find((existingRelation) => {
      if (
        getContentRelationTypeForKrcRelation(existingRelation.relationType) !== selectedRelationType
      ) {
        return false;
      }

      if (isSameRelation(existingRelation, relation)) {
        return false;
      }

      return existingRelation.targetHref === option.href;
    });

    return !duplicateRelation;
  });

  useEffect(() => {
    if (!targetHref) {
      return;
    }

    const hasSelectedGoal = filteredGoalOptions.some((option) => option.href === targetHref);
    if (!hasSelectedGoal) {
      setTargetHref(null);
    }
  }, [filteredGoalOptions, targetHref]);

  const handleSubmit = () => {
    if (!onSubmit || !targetHref) {
      return;
    }

    onSubmit({
      targetHref,
    });
    onClose();
  };

  const selectedGoal = filteredGoalOptions.find((option) => option.href === targetHref) || null;

  return (
    <div className="overflown-modal">
      <Modal.Header title={title || 'Directe coherentie'} onClose={onClose} />
      <Modal.Body>
        <div className="form-group">
          <label className="control-label">Doel</label>
          <KovResourcePicker
            localData={filteredGoalOptions}
            display="label"
            value={selectedGoal}
            clearable={false}
            onChange={(option: KrcGoalRelationOption | null) => {
              setTargetHref(option?.href || null);
            }}
            optionValue={(option: KrcGoalRelationOption) => option.href}
            filter={filterGoalOptions}
            menuPlacement="bottom"
            placeholder="Selecteer een doel"
            style={{
              option: (provided: Record<string, unknown>) => ({
                ...provided,
                cursor: 'pointer',
              }),
            }}
          />
          <p className="text-muted small">
            De doelen van het huidige domein worden weergegeven, typ om doelen van andere domeinen
            te zoeken.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-link pull-left" onClick={onClose}>
          Annuleren
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!targetHref}
          onClick={handleSubmit}
        >
          Ok
        </button>
      </Modal.Footer>
    </div>
  );
};

KrcGoalRelationModal.displayName = 'KrcGoalRelationModal';

export default KrcGoalRelationModal;
